import { motion, AnimatePresence } from "framer-motion";
import { X, Check, ArrowRight, Clock, Play, Download } from "lucide-react";
import Icon from "../Icon";
import GlowButton from "../ui-custom/GlowButton";
import { useData } from "../../hooks/useData";

// Animated service details modal. Renders any fields present in JSON:
// banner, gallery, faqs, documents, eligibility, processingTime, videoUrl, downloadLinks.
export default function ServiceModal({ service, onClose }) {
  const { data: links } = useData("links");
  return (
    <AnimatePresence>
      {service && (
        <motion.div
          className="fixed inset-0 z-[9000] flex items-center justify-center p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          data-testid="service-modal"
        >
          <div className="absolute inset-0 bg-slate-900/30 backdrop-blur-sm" onClick={onClose} />
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 40, scale: 0.96 }}
            transition={{ type: "spring", damping: 26, stiffness: 260 }}
            className="relative z-10 max-h-[86vh] w-full max-w-lg overflow-y-auto no-scrollbar rounded-3xl bg-white p-0 shadow-2xl"
          >
            {/* Banner */}
            <div className="relative h-36 overflow-hidden rounded-t-3xl bg-brand-gradient">
              {service.banner && <img src={service.banner} alt={service.title} className="h-full w-full object-cover opacity-80" />}
              <button onClick={onClose} className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-slate-700 hover:bg-white" data-testid="service-modal-close" aria-label="Close">
                <X size={18} />
              </button>
              <div className="absolute -bottom-6 left-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-brand shadow-lg">
                <Icon name={service.icon} size={26} />
              </div>
            </div>

            <div className="p-6 pt-9">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-brand-soft px-3 py-1 text-xs font-medium text-brand-2">{service.category}</span>
                {service.processingTime && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-600"><Clock size={12} /> {service.processingTime}</span>
                )}
              </div>
              <h3 className="mt-3 text-2xl font-bold text-slate-900">{service.title}</h3>
              <p className="mt-2 text-slate-500">{service.long}</p>

              {service.videoUrl && (
                <a href={service.videoUrl} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-brand" data-testid="service-video">
                  <Play size={16} /> Watch how it works
                </a>
              )}

              {service.gallery?.length > 0 && (
                <div className="mt-5 flex gap-2 overflow-x-auto no-scrollbar">
                  {service.gallery.map((g, i) => (
                    <img key={i} src={g} alt="" loading="lazy" className="h-20 w-28 shrink-0 rounded-xl object-cover" />
                  ))}
                </div>
              )}

              {service.features?.length > 0 && (
                <div className="mt-6">
                  <p className="text-sm font-semibold text-slate-900">Key features</p>
                  <ul className="mt-3 grid gap-2">
                    {service.features.map((f) => (
                      <li key={f} className="flex items-center gap-2 text-sm text-slate-600"><Check size={16} className="text-brand-2" /> {f}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                {service.eligibility && (
                  <div className="rounded-2xl bg-slate-50 p-4">
                    <p className="text-xs uppercase tracking-wide text-slate-400">Eligibility</p>
                    <p className="mt-1 text-sm text-slate-700">{service.eligibility}</p>
                  </div>
                )}
                <div className="rounded-2xl bg-slate-50 p-4">
                  <p className="text-xs uppercase tracking-wide text-slate-400">Pricing</p>
                  <p className="mt-1 text-sm font-semibold text-brand">{service.priceRef}</p>
                </div>
              </div>

              {service.documents?.length > 0 && (
                <div className="mt-3 rounded-2xl bg-slate-50 p-4">
                  <p className="text-xs uppercase tracking-wide text-slate-400">Documents required</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {service.documents.map((d) => (
                      <span key={d} className="rounded-full bg-white px-3 py-1 text-xs text-slate-600 shadow-sm">{d}</span>
                    ))}
                  </div>
                </div>
              )}

              {service.faqs?.length > 0 && (
                <div className="mt-3">
                  <p className="text-sm font-semibold text-slate-900">FAQs</p>
                  <div className="mt-2 space-y-2">
                    {service.faqs.map((f, i) => (
                      <details key={i} className="rounded-xl bg-slate-50 p-3 text-sm">
                        <summary className="cursor-pointer font-medium text-slate-700">{f.q}</summary>
                        <p className="mt-1.5 text-slate-500">{f.a}</p>
                      </details>
                    ))}
                  </div>
                </div>
              )}

              {service.downloadLinks?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {service.downloadLinks.map((d, i) => (
                    <a key={i} href={d.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:border-brand hover:text-brand">
                      <Download size={13} /> {d.label}
                    </a>
                  ))}
                </div>
              )}

              <GlowButton href={links?.register} to={!links ? "/contact" : undefined} className="mt-7 w-full" icon={<ArrowRight size={18} />} data-testid="service-modal-cta">
                {service.cta || "Get Started"}
              </GlowButton>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
